import * as React from 'react';
import { observer } from 'mobx-react';
import { action, observable, computed } from 'mobx';
import { Button } from 'react-bootstrap';

import { bind, inject } from '../../utils/decorators';
import { IAuftrag, AdressArt } from '../../models/ApiTypes';
import AuftragAdesse from './adressen/AuftragAdesse';
import VorgangListe from '../vorgaenge/VorgangListe';
import DocumentList from '../dokumente/DocumentList';
import ObjektListe from '../objekte/ObjektListe';
import { UiStore } from '../../stores/UiStore';
import Callout from '../site/Callout';
import FaIcon from '../site/FaIcon';
import LadeFortschritt from '../site/LadeFortschritt';
import AuftragBelegListe from './AuftragBelegListe';

interface IAuftragDetailsProps {
  auftragId: string;
  onAuftragAbschliessen: (auftrag: IAuftrag) => void;
  onAuftragZurueckstellen: (auftrag: IAuftrag) => void;
  onAuftragInArbeit: (auftrag: IAuftrag) => void;
  onAuftragObjektAuswahlAufheben: (auftrag: IAuftrag) => void;
  onFolgeAuftragAnlegen: (auftrag: IAuftrag) => void;
}

@observer
export default class AuftragDetails extends React.Component<IAuftragDetailsProps, {}> {

  @inject(UiStore) uiStore: UiStore;

  @observable auftrag: IAuftrag;

  componentDidMount() {
    this.refreshAuftrag();
  }

  componentWillReceiveProps(nextProps: IAuftragDetailsProps) {
    if (nextProps.auftragId !== this.props.auftragId) {
      this.loadAuftrag(nextProps.auftragId);
    }
  }

  async loadAuftrag(auftragId: string) {
    const response = await this.uiStore.apiStore.auftragDetails(auftragId);
    if (response.ok) {
      this.setAuftrag(response.json);
    }
  }

  @bind @action setAuftrag(auftrag: IAuftrag) {
    this.auftrag = auftrag;
  }

  @bind refreshAuftrag() {
    this.loadAuftrag(this.props.auftragId);
  }

  // Liefert den zur Auftragskategorie passenden Objektbezeichner
  // SEP für SES, AGT für Kal. Abgasmessgerät
  @computed get objektBezeichner() {
    let kategorie: string = this.auftrag.kategorie ? this.auftrag.kategorie : '';
    switch (kategorie.toLocaleLowerCase()) {
      case 'ses':
        return 'Scheinwerfer-Einstellprüfgerät';
      case 'agt':
        return 'Abgasmessgerät';
      case 'bps':
        return 'Bremsenprüfstand';
      default:
        return 'Objekt';
    }
  }

  /**
   * Liefert true wenn die Objektauswahl im Auftrag angezeigt werden soll
   * Hängt von der Kategorie des Auftrags bzw. dem Verfahren ab
   */
  @computed get objektAuswahl() {
    if (!this.auftrag.kategorie) {
      return true;
    }
    switch (this.auftrag.kategorie.toLocaleLowerCase()) {
      case 'agt':
        return false;
      case 'dmg':
        return false;
      default:
        return true;
    }
  }

  /** Erzeugt einen "MailTo"-HTML Link der eine Mail-Vorlage im installierten Mail-Programm öffnet */
  @computed get mailToLinkOeffentlicheFreigabe() {
    const titel = encodeURIComponent(`Ihre DIQ Zert Dokumente zu Auftrag ${this.auftrag.auftrag_name}`);
    const body = `Sehr geehrte Damen und Herren,%0D%0A%0D%0Adie Dokumente zu Ihrem Auftrag finden Sie nach Freigabe durch die DIQ Zert GmbH unter folgendem Link:%0D%0A${this.auftrag.oeffentlicherlink}`;
    return `mailto:?subject=${titel}&body=${body}`;
  }

  render() {
    let { onAuftragAbschliessen, onAuftragInArbeit, onAuftragZurueckstellen, onAuftragObjektAuswahlAufheben, onFolgeAuftragAnlegen } = this.props;
    const auftrag = this.auftrag;

    if (!auftrag) {
      return <LadeFortschritt/>;
    }

    const portalAdmin = this.uiStore.userProfile && this.uiStore.userProfile.portaladmin;

    return (
      <div className="maxwidth">
        <div>
          Referenznummer: {auftrag.refnr} <br />
          {this.uiStore.userProfile && this.uiStore.userProfile.projektleiter && (
            <span>Zugewiesen an: {auftrag.pi.vorname} {auftrag.pi.name} ({auftrag.pi.nummer})<br /></span>
          )}
          {auftrag.bemerkung && auftrag.bemerkung !== '-' && <div><FaIcon icon="file-text-o" /> {auftrag.bemerkung}</div>}
          {auftrag.referenzdatum} - <span className={`auftrag-${auftrag.auftragstatus.toLowerCase()}`}>{auftrag.statustext}</span>
          {!auftrag.abgeschlossen && (
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateTo(`details/aendern`)}>Angaben ändern</Button>
            </div>
          )}
        </div>

        {auftrag.abgeschlossen && (
          <div>
            <Callout bsStyle="info">
              <h4><FaIcon icon="info-circle" /> Dieser Auftrag ist bereits abgeschlossen</h4>
              <p>Ein Ändern der Daten ist erst nach erneuter Freigabe eines Mitarbeiters der DIQ Zert GmbH möglich.</p>
            </Callout>
            {/* Portaladministratoren (=interne Mitarbeiter) dürfen Folgeaufträge anlegen */}
            {portalAdmin &&
              <Callout bsStyle="success">
                <h4><FaIcon icon="clone" /> Folgeauftrag anlegen</h4>
                <p>Auf Basis der in diesem Auftrag erfassten Daten kann ein Folgeauftrag mit Datenübernahme angelegt werden.</p>
                <div className="text-right">
                  <Button bsStyle="success" type="submit" className="btn-group" onClick={() => onFolgeAuftragAnlegen(auftrag)}>Folgeauftrag anlegen</Button>
                </div>
              </Callout>
            }

          </div>
        )}

        {/* Link und Info zur öffentlichen Dokumentenfreigabe */}
        {auftrag.oeffentlicherlink &&
          <div>
            <h3>Öffentliche Dokumentenfreigabe</h3>
            <div>
              Nach Prüfung durch die DIQ Zert freigegebene Dokumente können über <a href={auftrag.oeffentlicherlink} target="_blank">diesen Link</a> (<a title="Per Mail versenden (Vorlage in Mail-Programm öffnen)" href={this.mailToLinkOeffentlicheFreigabe}><FaIcon icon="envelope-o" /> Mailvorlage</a>) ohne Zugangsdaten heruntergeladen werden.
            </div>
          </div>
        }

        <h3>Auftraggeber</h3>
        <AuftragAdesse auftrag={auftrag} adressArt={AdressArt.Auftraggeber}/>
        {!auftrag.abgeschlossen && (
          <div className="text-right">
            <Button onClick={() => this.uiStore.navigateTo(`adresse/auftraggeber`)}>Angaben ändern</Button>
          </div>
        )}

        <h3>Ort der Kalibrierung</h3>
        <AuftragAdesse auftrag={auftrag} adressArt={AdressArt.Beauftragter}/>
        {!auftrag.abgeschlossen && (
          <div className="text-right">
            <Button onClick={() => this.uiStore.navigateTo(`adresse/kalibrierort`)}>Angaben ändern</Button>
          </div>
        )}

        {this.objektAuswahl &&
          <div>
            <h3>{this.objektBezeichner}</h3>
            <ObjektListe
              showTitle={false}
              objekte={auftrag.objekte}
              editable={!auftrag.abgeschlossen}
              auftrag={auftrag}
              onAuswahlAufheben={() => onAuftragObjektAuswahlAufheben(auftrag)}
            />
          </div>
        }

        <h3>Dienstleistungen</h3>
        {auftrag.dienstleistungen.map((dienstleistung, index) => (
          <VorgangListe key={index} dienstleistung={dienstleistung} vorgaenge={auftrag.vorgaenge} />
        ))}
        {!auftrag.abgeschlossen && auftrag.inarbeit && (
          <div className="text-right">
            <Button onClick={() => this.uiStore.navigateTo(`neuedienstleistung`)}>Neue Dienstleistung</Button>
          </div>
        )}

        <h3>Workflow</h3>
        {!auftrag.abgeschlossen && auftrag.inarbeit && (
          <Callout bsStyle="warning">
            <h4>Auftrag abschließen</h4>
            <p>Durch das Abschließen wird ein Mitarbeiter des DIQ Zert GmbH informiert und der Auftrag für weitere Bearbeitungen gesperrt.</p>
            {((!auftrag.auftraggeber_strasse) || (!auftrag.auftraggeber_plz) || (!auftrag.auftraggeber_ort)) &&
              <p><b>Damit der Auftrag abgeschlossen werden kann, müssen noch folgende Angaben zum Auftraggeber gemacht werden:</b></p>
            }
            <ul>
              {(!auftrag.auftraggeber_strasse) && <li>Straße</li>}
              {(!auftrag.auftraggeber_plz) && <li>PLZ</li>}
              {(!auftrag.auftraggeber_ort) && <li>Ort</li>}
            </ul>
            {(auftrag.auftraggeber_strasse) && (auftrag.auftraggeber_plz) && (auftrag.auftraggeber_ort) &&
              <div className="text-right">
                <Button bsStyle="warning" className="btn-group" type="submit" onClick={() => onAuftragAbschliessen(auftrag)}>Auftrag abschließen</Button>
              </div>
            }
          </Callout>
        )}

        {/* Arbeit an zurückgestelltem Auftrag fortsetzen */}
        {!auftrag.abgeschlossen && (auftrag.auftragstatus === 'zurueck') && (
          <Callout bsStyle="info">
            <h4>Auftrag weiterbearbeiten</h4>
            <p>Der Auftrag wird für die erneute Bearbeitung freigeschaltet.</p>
            <div className="text-right">
              <Button bsStyle="info" className="btn-group" type="submit" onClick={() => onAuftragInArbeit(auftrag)}>Auftrag weiterbearbeiten</Button>
            </div>
          </Callout>
        )}

        {/* Arbeit an Auftrag in Angebotsphase starten */}
        {!auftrag.abgeschlossen && (['angebot', 'auftert', 'auftbest', 'geplant', 'termin'].indexOf(auftrag.auftragstatus) >= 0) && (
          <Callout bsStyle="success">
            <h4>Bearbeitung starten</h4>
            <p>Ändert den Status des Auftrags auf "in Arbeit", es können weitere Dienstleistungen eingefügt und abgearbeitet werden.</p>
            <div className="text-right">
              <Button bsStyle="success" className="btn-group" type="submit" onClick={() => onAuftragInArbeit(auftrag)}>Bearbeitung starten</Button>
            </div>
          </Callout>
        )}

        {!auftrag.abgeschlossen && auftrag.inarbeit && (
          <Callout bsStyle="info">
            <h4>Auftrag zurückstellen</h4>
            <p>Durch das Zurückstellen kann der Auftrag zu einem späteren Zeitpunkt weiterbearbeitet werden.</p>
            <div className="text-right">
              <Button bsStyle="info" className="btn-group" type="submit" onClick={() => onAuftragZurueckstellen(auftrag)}>Auftrag zurückstellen</Button>
            </div>
          </Callout>
        )}

        <DocumentList header="headline" source="auftrag" id={auftrag.anr} />
        <AuftragBelegListe anr={auftrag.anr} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/AuftragDetails.tsx